import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, HelpCircle, Scale, X } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { ErrorBox, Loading, useLoad } from "@/components/nds/windows/finance/parts";

const ORDER = [["headline", "a"], ["ledger", "b"], ["statement", "c"], ["invoice_report", "d"]];
const TONE = { Yes: "text-amber-300 border-amber-400/40", No: "text-emerald-300 border-emerald-400/30", "Not checked": "text-nex-muted border-white/15" };

/** The button next to the period toggle. Runs the read-only check only when clicked. */
export function SourceCheckButton({ onRun, busy }) {
  return (
    <button type="button" onClick={onRun} disabled={busy} data-testid="flow-source-check-btn"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-nex-gold/50 text-nex-gold font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-nex-gold/10 transition-colors disabled:opacity-40">
      <Scale className="h-3.5 w-3.5" />Check against sources
    </button>
  );
}

function Entries({ k, e, onClose }) {
  return (
    <div data-testid={`flow-sc-entries-${k}`} className="mt-2 border border-white/10 rounded-sm">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 text-[11px]">
        <span className="flex-1 text-nex-silver">{e.count} entries · total <b className="font-mono">{money(e.total)}</b>{e.count > e.shown ? ` · showing first ${e.shown}` : ""}</span>
        <button type="button" onClick={onClose} data-testid={`flow-sc-entries-close-${k}`} className="text-nex-muted hover:text-nex-gold"><X className="h-3.5 w-3.5" /></button>
      </div>
      {!e.rows.length ? <div className="px-3 py-3 text-xs text-nex-muted">No entries behind this number.</div> :
        <div className="max-h-72 overflow-auto"><table className="w-full text-[11px]"><thead className="sticky top-0 bg-[#0B1220]"><tr className="text-left text-nex-muted font-mono uppercase text-[9px] tracking-[0.14em]">
          {["Date", "Resident", "Bucket", "Amount", "Type", "Source id"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
          <tbody>{e.rows.map((r, i) => (
            <tr key={`${r.source_id}-${i}`} className="border-t border-white/5" title={r.note}>
              <td className="px-2 py-1 font-mono whitespace-nowrap">{r.date}</td><td className="px-2 py-1">{r.resident}</td><td className="px-2 py-1 text-nex-muted">{r.bucket}</td>
              <td className="px-2 py-1 font-mono text-right whitespace-nowrap">{money(r.amount)}</td><td className="px-2 py-1 text-nex-muted">{r.type}</td>
              <td className="px-2 py-1 font-mono text-nex-muted break-all">{r.source_id}</td></tr>))}</tbody></table></div>}
    </div>
  );
}

function NumberCard({ k, letter, n, open, onToggle }) {
  return (
    <div data-testid={`flow-sc-num-${k}`} className={`p-3 rounded-md border space-y-1 ${n.available ? "border-white/15 bg-white/[0.03]" : "border-dashed border-white/15"}`}>
      <div className="nds-label-dim text-[9px]">{letter} · {n.label}</div>
      {n.available ? <div className="font-mono text-lg text-nex-silver tabular-nums" data-testid={`flow-sc-amount-${k}`}>{money(n.amount)}</div>
        : <div className="text-sm text-nex-muted" data-testid={`flow-sc-amount-${k}`}>Not available for this period</div>}
      {k === "ledger" && <div className="text-[11px] text-nex-muted" data-testid="flow-sc-ledger-detail">In {money(n.in)} · Out {money(n.out)} · {n.count} entries</div>}
      {n.landlord_paid != null && <div className="text-[11px] text-nex-muted">Landlord payments in the report this period {money(n.landlord_paid)}</div>}
      <div className="text-[10px] text-nex-muted/80 leading-snug">{n.available ? n.source : n.reason}</div>
      {n.warning && <div className="text-[10px] text-amber-300 flex gap-1" data-testid={`flow-sc-warn-${k}`}><AlertTriangle className="h-3 w-3 shrink-0 mt-px" />{n.warning}</div>}
      {n.available && <button type="button" onClick={onToggle} data-testid={`flow-sc-show-${k}`} className="text-[11px] text-nex-gold hover:underline">{open ? "Hide the entries" : "Show the entries behind this number"}</button>}
    </div>
  );
}

function Reasons({ rows }) {
  return (
    <div className="space-y-1.5" data-testid="flow-sc-reasons">
      <div className="nds-label-dim text-[9px]">Why they might differ</div>
      {rows.map((x) => (
        <div key={x.key} data-testid={`flow-sc-reason-${x.key}`} className="flex gap-2 text-[11px]">
          <span className={`shrink-0 w-[5.5rem] text-center px-1.5 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-[0.12em] ${TONE[x.answer]}`}>{x.answer}</span>
          <div className="min-w-0"><div className="text-nex-silver">{x.label}{x.amount != null && <span className="font-mono text-nex-muted"> · {money(x.amount)}</span>}</div>
            <div className="text-nex-muted">{x.text}{x.note ? ` · ${x.note}` : ""}</div></div>
        </div>))}
    </div>
  );
}

/** Read-only 'Check against sources' panel for the open tab and period. */
export function SourceCheckPanel({ tab, params, onClose }) {
  const [state, reload] = useLoad(() => finApi.get("/flow/source-check", { tab, ...params }), []);
  const [open, setOpen] = useState(null);
  const d = state.data;
  const ok = d?.verdict.state.startsWith("all_match");
  return (
    <section data-testid="flow-source-check" className="mb-5 p-4 rounded-md border border-nex-gold/40 bg-nex-gold/[0.03] space-y-4">
      <div className="flex items-center gap-2">
        <Scale className="h-4 w-4 text-nex-gold" />
        <span className="flex-1 text-nex-silver font-semibold">Check against sources{d ? ` · ${d.tab_label} · ${d.date_from} to ${d.date_to}` : ""}</span>
        <button type="button" onClick={onClose} data-testid="flow-sc-close" className="text-nex-muted hover:text-nex-gold"><X className="h-4 w-4" /></button>
      </div>
      {state.loading && !d && <Loading testid="flow-sc-loading" label="Checking against sources…" />}
      {state.error && <ErrorBox testid="flow-sc-error" error={state.error} onRetry={reload} />}
      {d && <>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{ORDER.map(([k, l]) => <NumberCard key={k} k={k} letter={l} n={d.numbers[k]} open={open === k} onToggle={() => setOpen(open === k ? null : k)} />)}</div>
        {open && <Entries k={open} e={d.numbers[open].entries} onClose={() => setOpen(null)} />}
        <div data-testid="flow-sc-verdict" className={`flex gap-2 p-3 rounded-sm border text-sm ${ok ? "border-emerald-400/30 text-emerald-200" : "border-amber-400/40 text-amber-100"}`}>
          {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" />}{d.verdict.text}</div>
        <Reasons rows={d.reasons} />
        <div className="text-[10px] text-nex-muted">Read-only. Nothing in the ledger, balances or report totals was changed. Times in {d.timezone}.</div>
      </>}
    </section>
  );
}
